<template>
  <SectionWithAnchor :anchorId="AnchorLinks.PriceDevelopment" class="row">
    <div :class="[$style.container, 'col-md-10 col-lg-8']">
      <div :class="$style.heading">
        <h2>{{ $t('localLanding.priceDevelopment.title') }}</h2>
        <HgIcon
          icon="InfoCircle"
          color="gray"
          :class="$style.infoIcon"
          data-testid="info-icon"
          @click="handleModalOpen"
        />
      </div>
      <FiltersBar />
      <div :class="[$style.priceWrapperIs24, { [$style.priceWrapperHg]: !isIs24 }]">
        <PriceValue />
        <PriceTrends
          :priceTrendsData="priceData.priceTrends"
          :isLoading="isPriceDataLoading || isResolvingLocation"
          :propertyType="propertyType"
          @onRangeChange="handleRangeChange"
        />
      </div>
      <p :class="$style.summary" v-html="summaryDescription" />
      <HgModal
        id="price-development-modal"
        v-model="isModalVisible"
        mode="dialog"
        @close="isModalVisible = false"
      >
        <template #title> {{ $t('localLanding.tooltips.title.priceTrends') }}</template>
        <div
          v-for="{ title, description } in modalContent"
          :key="title"
          :class="$style.tooltipContentWrapper"
        >
          <p :class="$style.tooltipContentHeading">{{ title }}</p>
          <p :class="$style.tooltipContentDescription" v-html="description" />
        </div>
      </HgModal>
    </div>
  </SectionWithAnchor>
</template>

<script setup lang="ts">
import PriceTrends from '@/components/common/PriceTrends/PriceTrends.vue';
import SectionWithAnchor from '@/components/common/SectionWithAnchor/SectionWithAnchor.vue';
import { getLocationInfos, LocationInfos } from '@/helpers/GeoLocation/GeoLocationHelper';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingMapInteractionEvent } from '@/tracking/tracking';
import { LOCAL_LANDING_PAGE_ACTION } from '@/tracking/types';
import { AnchorLinks, PriceTrendsRangeSummary, TEXT_PLACEHOLDER } from '@/types';
import { HgIcon, HgModal, HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FiltersBar from './FiltersBar.vue';

const DEFAULT_SUMMARY: PriceTrendsRangeSummary = {
  range: 0,
  year: TEXT_PLACEHOLDER,
  evolution: TEXT_PLACEHOLDER,
  evolutionType: 'increase',
};

// Composables
const localLandingStore = useLocalLandingStore();
const { t, locale } = useI18n();
const { isPriceDataLoading, isResolvingLocation, propertyType, selectedLocation, priceData } =
  storeToRefs(localLandingStore);

// Reactive data
const isModalVisible = ref(false);
const summary = ref<PriceTrendsRangeSummary>(DEFAULT_SUMMARY);
const isIs24 = isPlatformIs24();

// Other computed properties
const selectedLocationInfos = computed((): LocationInfos => {
  return getLocationInfos(selectedLocation.value, locale.value as HgSupportedLanguage);
});

const summaryDescription = computed((): string => {
  const level = selectedLocation.value?.type === 'canton' ? 'canton' : 'muniandPost';
  return t(`priceTrends.summary.${propertyType.value}.${level}`, {
    location: selectedLocationInfos.value.name || TEXT_PLACEHOLDER,
    year: summary.value.year,
    percent: summary.value.evolution,
    trend: t(`localLanding.priceTrends.trend.${summary.value.evolutionType}`),
  });
});

const modalContent = computed(() => {
  return [
    {
      title: t('localLanding.tooltips.dataUpdatePriceTrendsTitle'),
      description: t('localLanding.tooltips.dataUpdatePriceTrends'),
    },
    {
      title: t('localLanding.tooltips.marketVsOfferedTitle'),
      description: t('localLanding.tooltips.marketVsOffered'),
    },
    {
      title: t('localLanding.tooltips.medianPriceTitle'),
      description: t('localLanding.tooltips.medianPrice'),
    },
    {
      title: t('localLanding.tooltips.hedonicValuationTitle'),
      description: t('localLanding.tooltips.hedonicValuation'),
    },
  ];
});

// Dynamic component based on platform
const PriceValue = isPlatformIs24()
  ? defineAsyncComponent(
      () => import('@/components/local-landing-page/PriceDevelopment/PriceValueIs24.vue'),
    )
  : defineAsyncComponent(
      () => import('@/components/local-landing-page/PriceDevelopment/PriceValueHg.vue'),
    );

// Watchers
watch(isPriceDataLoading, (isLoading) => {
  if (isLoading) {
    summary.value = DEFAULT_SUMMARY;
  }
});

// Methods
const handleModalOpen = () => {
  isModalVisible.value = true;
};

const handleRangeChange = (value: PriceTrendsRangeSummary) => {
  summary.value = value;
  handleTrackingEvent(+value.range);
};

const handleTrackingEvent = (range: number) => {
  const labelValue = range === 0.5 ? `6_months` : range === 1 ? `1_year` : `${range}_years`;
  trackingMapInteractionEvent(LOCAL_LANDING_PAGE_ACTION.PRICE_TREND, labelValue);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$price-section-height-is24: 337px;
$price-section-height-hg-md: 292px;
$price-section-height-hg-sm: 331px;

.container {
  display: flex;
  flex-direction: column;
  gap: $space-200;

  @include grid-media-query(sm) {
    margin-inline: auto;
  }
}

.heading {
  display: flex;
  align-items: center;

  h2 {
    margin-right: $space-50;

    @include vertical-margin;
    @include heading-2;
  }

  .infoIcon {
    cursor: pointer;
  }
}

.priceWrapperIs24 {
  display: flex;
  flex-direction: column;
  gap: $space-150;

  @include grid-media-query(sm) {
    flex-direction: row;
    height: $price-section-height-is24;
  }

  > div {
    flex: 1;
  }
}

.priceWrapperHg {
  @include grid-media-query(sm) {
    height: $price-section-height-hg-sm;
  }

  @include grid-media-query(md) {
    height: $price-section-height-hg-md;
  }

  > div {
    flex: 1;
  }
}

.summary {
  @include vertical-margin;
  @include body-2($color-grey-100);
}

.tooltipContentWrapper {
  margin-bottom: $space-250;
}

.tooltipContentDescription {
  @include vertical-margin;
  @include body-2;
}

.tooltipContentHeading {
  @include vertical-margin;
  @include heading-5;
}
</style>
